import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon, ArrowRightIcon } from 'lucide-react';
interface ImageInfo {
  src: string;
  caption: string;
}
interface TransformationShowcaseProps {
  title: string;
  beforeImage: ImageInfo;
  afterImage: ImageInfo;
  imagePosition?: string;
}
export const TransformationShowcase: React.FC<TransformationShowcaseProps> = ({
  title,
  beforeImage,
  afterImage,
  imagePosition = 'center'
}) => {
  const [showAfter, setShowAfter] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [hasTransitioned, setHasTransitioned] = useState(false);
  // Preload after image
  useEffect(() => {
    const img = new Image();
    img.src = afterImage.src;
  }, [afterImage.src]);
  const toggleImage = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setShowAfter(!showAfter);
    setHasTransitioned(true);
    setTimeout(() => {
      setIsTransitioning(false);
    }, 450);
  };
  return <div className="bg-gray-900 rounded-xl overflow-hidden shadow-xl luxury-card" data-id="element-1442">
      <div className="p-4 border-b border-gray-800" data-id="element-1443">
        <h4 className="text-xl font-bold text-center" data-id="element-1444">{title}</h4>
      </div>
      <div className="relative h-[500px] overflow-hidden" data-id="element-1445">
        <AnimatePresence mode="wait" data-id="element-1446">
          <motion.div key={showAfter ? 'after' : 'before'} initial={{
          opacity: hasTransitioned || showAfter ? 0 : 1
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.45,
          ease: 'easeInOut'
        }} className="absolute inset-0" data-id="element-1447">
            <img src={showAfter ? afterImage.src : beforeImage.src} alt={showAfter ? 'Po' : 'Před'} className="w-full h-full object-contain bg-gray-800" style={{
            objectPosition: imagePosition
          }} data-id="element-1448" />
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-4" data-id="element-1449">
              <motion.p initial={{
              y: 20,
              opacity: 0
            }} animate={{
              y: 0,
              opacity: 1
            }} transition={{
              delay: 0.15,
              duration: 0.3
            }} className="text-white text-lg font-medium" data-id="element-1450">
                {showAfter ? afterImage.caption : beforeImage.caption}
              </motion.p>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Luxurious overlay indicator */}
        <div className="absolute top-4 right-4 bg-black/70 backdrop-blur-sm px-3 py-1 rounded-full" data-id="element-1451">
          <span className={`text-sm font-medium ${showAfter ? 'text-green-500' : 'text-gray-400'}`} data-id="element-1452">
            {showAfter ? 'PO' : 'PŘED'}
          </span>
        </div>

        {/* Progress line at the bottom */}
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-800" data-id="element-1453">
          <motion.div className="h-full bg-gradient-to-r from-green-600 to-green-400" initial={{
          width: showAfter ? '100%' : '0%'
        }} animate={{
          width: showAfter ? '100%' : '0%'
        }} transition={{
          duration: 0.4
        }} data-id="element-1454" />
        </div>
      </div>

      {/* Elegant controls */}
      <div className="p-4 flex items-center justify-between" data-id="element-1455">
        <button onClick={toggleImage} disabled={isTransitioning} className="group flex items-center space-x-2 transition-all duration-300 px-3 py-1 rounded-full bg-gray-800/80 hover:bg-green-600/20" data-id="element-1456">
          <span className="text-sm font-medium group-hover:text-green-500" data-id="element-1457">
            {showAfter ? 'Zobrazit před' : 'Zobrazit po'}
          </span>
          {showAfter ? <ArrowLeftIcon className="w-4 h-4 text-green-500" data-id="element-1458" /> : <ArrowRightIcon className="w-4 h-4 text-green-500" data-id="element-1459" />}
        </button>

        {/* Progress dots */}
        <div className="flex space-x-2" data-id="element-1460">
          <button onClick={() => !isTransitioning && setShowAfter(false)} className={`w-3 h-3 rounded-full transition-all duration-300 ${!showAfter ? 'bg-green-500 scale-110' : 'bg-gray-600 hover:bg-gray-500'}`} aria-label="Před" disabled={isTransitioning} data-id="element-1461" />
          <button onClick={() => !isTransitioning && setShowAfter(true)} className={`w-3 h-3 rounded-full transition-all duration-300 ${showAfter ? 'bg-green-500 scale-110' : 'bg-gray-600 hover:bg-gray-500'}`} aria-label="Po" disabled={isTransitioning} data-id="element-1462" />
        </div>
      </div>
    </div>;
};